/*
 * 比較検討機能の管理マネージャー
 */
const CompareManager = (() => {
  const KEY = "compareProducts";
  const MAX = 4;

  const get = () => JSON.parse(localStorage.getItem(KEY) || "[]");
  const set = (list) => localStorage.setItem(KEY, JSON.stringify(list));

  function add(handle) {
    const list = get();
    let next, action;

    if (list.includes(handle)) {
      next = list.filter((h) => h !== handle);
      action = "remove";
    } else {
      if (list.length >= MAX) return { ok: false, reason: "limit", list };
      next = [...list, handle];
      action = "add";
    }

    set(next);

    document.dispatchEvent(
      new CustomEvent("compare:changed", { detail: { list: next } })
    );
    document.dispatchEvent(
      new CustomEvent(`compare:${action}`, { detail: { handle, list: next } })
    );

    return { ok: true, action, list: next };
  }

  function remove(handle) {
    const next = get().filter((h) => h !== handle);
    set(next);
    document.dispatchEvent(
      new CustomEvent("compare:changed", { detail: { list: next } })
    );
    document.dispatchEvent(
      new CustomEvent("compare:remove", { detail: { handle, list: next } })
    );
    return { ok: true, action: "remove", list: next };
  }

  return { get, add, remove, MAX };
})();

/*
 * ボタン同期ユーティリティ
 */
const CompareButtonRegistry = new Map(); // handle => Set<HTMLButtonElement>

function registerCompareButton(handle, btn) {
  let set = CompareButtonRegistry.get(handle);
  if (!set) {
    set = new Set();
    CompareButtonRegistry.set(handle, set);
  }
  set.add(btn);

  // ノードが破棄されたら自動クリーンアップ
  const observer = new MutationObserver(() => {
    if (!document.contains(btn)) {
      set.delete(btn);
      if (set.size === 0) CompareButtonRegistry.delete(handle);
      observer.disconnect();
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function syncButtonState(btn, isSelected) {
  btn.classList.toggle("btn--compare-active", isSelected);
  btn.classList.toggle("active", isSelected);
  btn.setAttribute("aria-pressed", String(isSelected));
  // console.log("toggled", btn);
}

function syncAllButtonsFromStorage(
  root = document,
  list = CompareManager.get()
) {
  const selected = new Set(list);
  // console.log("Syncing compare buttons:", selected);
  root
    .querySelectorAll("[data-compare-btn][data-product-handle]")
    .forEach((btn) => {
      const handle = btn.dataset.productHandle;
      syncButtonState(btn, selected.has(handle));
    });
}

function syncButtonsByHandle(handle, isSelected) {
  const set = CompareButtonRegistry.get(handle);
  const all = new Set(set || []);
  // CSS.escape の存在を前提に
  document
    .querySelectorAll(
      `[data-compare-btn][data-product-handle="${CSS.escape(handle)}"]`
    )
    .forEach((btn) => all.add(btn));

  all.forEach((btn) => syncButtonState(btn, isSelected));
}

/*
 * 商品カードを強化
 */
function enhanceProductCards(container) {
  if (!container) return;

  const buttons = container.querySelectorAll(
    "[data-compare-btn][data-product-handle]"
  );
  const selected = new Set(CompareManager.get());
  const isComparePage = document.body.dataset.pageCompare === "1";

  buttons.forEach((btn) => {
    if (btn.dataset.compareBound === "1") return; // 二重バインド防止
    btn.dataset.compareBound = "1";

    const handle = btn.dataset.productHandle;
    registerCompareButton(handle, btn);

    // 初期状態を同期
    syncButtonState(btn, selected.has(handle));

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      // console.log("Compare button clicked:", handle);

      const res = CompareManager.add(handle);
      if (!res.ok && res.reason === "limit") {
        limitModal(CompareManager.MAX);
        return;
      }

      // 同一 handle の全ボタンを即時更新
      syncButtonsByHandle(handle, res.action === "add");

      // 比較ページならリスト更新イベントを発火
      if (document.body.dataset.pageCompare === "1") {
        document.dispatchEvent(
          new CustomEvent("compare:refresh", { detail: { list: res.list } })
        );
      }

      // 追加のときだけモーダルを表示（remove では表示しない）
      if (res.action === "add" && !isComparePage) {
        const product = getProductDetailsFromCard(btn);
        addModal(product);
      }
    });
  });
}

/*
 * 起動と動的セクション対応
 */
function bootIn(root) {
  const base = root && root.nodeType === 1 ? root : document;
  base
    .querySelectorAll("[data-product-cards-root]")
    .forEach(enhanceProductCards);
}

document.addEventListener("shopify:section:load", (e) => bootIn(e.target));
document.addEventListener("product-cards:ready", (e) => bootIn(e.target));

/*
 * ストレージ変化を全体に反映
 */
document.addEventListener("compare:add", (e) => {
  const { handle } = e.detail;
  syncButtonsByHandle(handle, true);
});
document.addEventListener("compare:remove", (e) => {
  const { handle } = e.detail;
  syncButtonsByHandle(handle, false);
});
document.addEventListener("compare:changed", (e) => {
  // list を受け取りつつ document 全体で同期
  syncAllButtonsFromStorage(document, e.detail?.list);
});

/*
 * モーダル表示関数
 */
function addModal(product) {
  CompareModal.showAdd({
    ...product,
  });
}
function limitModal(max) {
  CompareModal.showLimit(max);
}

/* 他タブ同期
 */
window.addEventListener("storage", (e) => {
  if (e.key === "compareProducts") {
    const list = JSON.parse(e.newValue || "[]");
    syncAllButtonsFromStorage(document, list);
  }
});

/*
 *  モーダル実装（HTML/JSのみ）
 */
const CompareModal = (() => {
  let root = null;
  let lastFocused = null;

  function injectStyleOnce() {
    if (document.getElementById("compare-modal-style")) return;
    const css = `
      .cmp-modal[hidden]{display:none}
      .cmp-modal{
        position:fixed; inset:0; z-index:9999;
        display:flex; align-items:center; justify-content:center;
        opacity: 0;
        transition: opacity 1.5s;
      }
      .cmp-modal.is-open{
        opacity: 1;
      }
      .cmp-modal__panel{
        display: flex;
        flex-direction: column;
        justify-content: center;
        width:min(560px, 92vw);
        align-items: center;
        background:#fff; box-shadow:0 20px 40px rgba(0,0,0,.2);
        padding:45px 20px 45px; outline: none;
        position: relative;
        transition: opacity 1s, transform 1s;
        opacity: 0;
      }
      .cmp-modal.is-open .cmp-modal__panel{
        opacity: 1;
        transform: translateY(0) scale(1);
      }
      .cmp-prod {
        display: flex;
        gap: 1.5rem;
        margin-top: 1.5rem;
        align-items: center;
        flex-direction: column;
        justify-content: center;
      }
        .cmp-prod__meta {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      .cmp-prod__title {
        font-size: 1.5rem;
      }
      .cmp-modal__close {
      position: absolute;
      padding: 0;
      top: 1rem;right: 1rem;}
      .cmp-prod__price {display: flex; align-items: center; justify-content: center; font-size: 1rem;}
      .cmp-modal__head{display:flex; align-items:center; gap:12px; margin-bottom:8px;}
      .cmp-modal__title{font-size:1.25rem; margin:0;}
      .cmp-modal__body{font-size:14px; line-height:1.6; margin:0 0 16px;display: flex;
        gap: 1.5rem;flex-direction: column;}
      .cmp-modal__actions{display:flex; gap:8px; justify-content:flex-end;}
      .cmp-modal__actions > .btn {font-size: 1rem !important;}
      .cmp-modal.is-add .cmp-modal__panel,
      .cmp-modal.is-limit .cmp-modal__panel{aspect-ratio: 1/1;}
    `;
    const style = document.createElement("style");
    style.id = "compare-modal-style";
    style.textContent = css;
    document.head.appendChild(style);
  }

  function ensure() {
    if (root) return root;
    injectStyleOnce();

    root = document.createElement("div");
    root.className = "cmp-modal";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("hidden", "");

    root.innerHTML = `
      <div class="cmp-modal__panel" tabindex="-1" aria-labelledby="cmp-modal-title" aria-describedby="cmp-modal-desc">
        <button class="cmp-modal__close">
          <img src="//cdn.shopify.com/s/files/1/0653/2762/8536/files/img_icon-clause.svg?v=1756270205" width="30" height="30" alt="Close Icon" loading="eager">
        </button>  
      <div class="cmp-modal__head">
          <h2 id="cmp-modal-title" class="cmp-modal__title"></h2>
        </div>
        <div id="cmp-modal-desc" class="cmp-modal__body"></div>
        <div class="cmp-modal__actions">
          <a href="/pages/model-comparison/" class="btn" data-primary>比較を見る</a>
        </div>
      </div>
    `;
    document.body.appendChild(root);

    // 閉じる系
    root.addEventListener("click", (e) => {
      if (e.target === root) close(); // オーバーレイクリック
    });
    const closeButton = document.querySelector(".cmp-modal__close");
    closeButton.addEventListener("click", close);
    root.querySelector("[data-primary]")?.addEventListener("click", close);
    document.addEventListener("keydown", onKeyDown);

    return root;
  }

  function focusTrap(e) {
    if (e.key !== "Tab") return;
    const panel = root.querySelector(".cmp-modal__panel");
    const focusables = panel.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeyDown(e) {
    if (root?.hasAttribute("hidden")) return;
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
    if (e.key === "Tab") focusTrap(e);
  }

  function open({ mode, title, message, product }) {
    const el = ensure();
    lastFocused = document.activeElement;

    el.classList.toggle("is-add", mode === "add");
    el.classList.toggle("is-limit", mode === "limit");
    el.querySelector("#cmp-modal-title").textContent = title;
    const body = el.querySelector("#cmp-modal-desc");
    if (product) {
      body.innerHTML = `
        <div class="cmp-prod">
          ${
            product.image
              ? `<img src="${product.image}" alt="" width="200" height="200" style="border-radius:8px;object-fit:cover;">`
              : ""
          }
          <div class="cmp-prod__meta" style="min-width:0">
            ${
              product.collection
                ? `<div class="cmp-prod__collection" style="font-size:12px;opacity:.8">${product.collection}</div>`
                : ""
            }
            <div class="cmp-prod__title" style="font-weight:600">${
              product.title || product.handle
            }</div>
            ${
              product.priceHTML
                ? `<div class="cmp-prod__price">${product.priceHTML}円（税込）</div>`
                : ""
            }
          </div>
        </div>
        <div class="cmp-help" style="margin-top:10px;font-size:13px">${
          message || ""
        }</div>
      `;
    } else {
      body.innerHTML =`
      <p style="font-size:20px; text-align: center;width: min(100%, 296px);margin-inline: auto;">比較できるのは<br> 4モデルとなっております。</p>
      `
    }

    el.removeAttribute("hidden");
    // アニメーション開始のために少し遅延
    requestAnimationFrame(() => {
      el.classList.add("is-open");
      // パネルへフォーカス
      el.querySelector(".cmp-modal__panel").focus();
    });
  }

  function close() {
    if (!root) return;
    root.classList.remove("is-open");
    // トランジション終了後に hidden を設定
    root.addEventListener('transitionend', function handler(e) {
      if (e.propertyName === 'opacity' && e.target === root) {
        root.setAttribute("hidden", "");
        root.removeEventListener('transitionend', handler);
        if (lastFocused && document.contains(lastFocused)) {
          lastFocused.focus();
        }
      }
    });
  }

  return {
    showAdd(product) {
      open({
        mode: "add",
        title: "比較対象に追加されました",
        product,
        message: `2～4本のモデルを選んで比較してください。`,
      });
    },
    showLimit(max) {
      open({
        mode: "limit",
        title: "",
        message: `比較できるのは ${max} モデルとなっております。`,
      });
    },
    close,
  };
})();

/*
 * クリック元のボタンからカード情報を抜き取る
 */
// ボタンから商品カード情報を抽出（dataset > DOM の順で優先）。取りこぼしに強い版
// 画像URLを賢く決める（lazy対応・srcsetから近い幅優先）
function pickSrcFromImg(img, targetWidth = 720) {
  if (!img) return "";
  if (img.currentSrc) return img.currentSrc;
  if (img.src) return img.src;
  const raw =
    img.getAttribute("srcset") || img.getAttribute("data-srcset") || "";
  if (!raw) return "";
  const items = raw
    .split(",")
    .map((s) => {
      const [url, w] = s.trim().split(/\s+/);
      const width = parseInt((w || "").replace("w", ""), 10) || 0;
      return { url, width };
    })
    .filter((i) => i.url);
  if (!items.length) return "";
  const over = items
    .filter((i) => i.width >= targetWidth)
    .sort((a, b) => a.width - b.width)[0];
  return (over || items.sort((a, b) => b.width - a.width)[0]).url;
}

function getProductDetailsFromCard(btn) {
  const ds = btn.dataset || {};
  const handle = ds.productHandle || "";

  // まず dataset
  let title = ds.product || ds.productTitle || "";
  let image = ds.productImg || ds.productImage || "";
  let collection = ds.productCollection || ""; // 基本空想定（後でDOMから補完）
  let priceHTML = ds.productPrice || "";

  // カードDOM
  const card =
    btn.closest("[data-product-handle]") ||
    btn.closest(
      ".grid-product, .grid-product__content, .product-card, .grid__item, [data-product-id]"
    ) ||
    null;

  return {
    handle,
    title: title || handle,
    collection,
    priceHTML,
    image,
  };
}

document.addEventListener("click", (e) => {
  const btn = e.target.closest("[data-compare-btn][data-product-handle]");
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const handle = btn.dataset.productHandle;
  const res = CompareManager.add(handle);

  if (!res.ok && res.reason === "limit") {
    limitModal(CompareManager.MAX);
    return;
  }

  // 同一ハンドルの全ボタンを即時同期
  syncButtonsByHandle(handle, res.action === "add");

  // 比較ページならリスト更新
  if (document.body.dataset.pageCompare === "1") {
    document.dispatchEvent(
      new CustomEvent("compare:refresh", { detail: { list: res.list } })
    );
  }

  // 追加時のみモーダル
  if (res.action === "add" && document.body.dataset.pageCompare !== "1") {
    const product = getProductDetailsFromCard(btn);
    addModal(product);
  }
});

function syncComparisonIconActive() {
  const navIcon = document.querySelector('.site-nav__link--icon-comparison');
  const drawerIcon = document.querySelector('.drawer_header-item-comparison');
  if (!navIcon) return;
  if (!drawerIcon) return;

  const list = CompareManager.get();
  navIcon.classList.toggle('active', list.length > 0);
  drawerIcon.classList.toggle('active', list.length > 0);
}

// ストレージ変化時にも同期
window.addEventListener('storage', (e) => {
  if (e.key === 'compareProducts') {
    syncComparisonIconActive();
    // ...既存のstorageイベント処理...
  }
});

// 比較リスト変更時にも同期
document.addEventListener('compare:changed', () => {
  syncComparisonIconActive();
});

// 初期化時にも同期
document.addEventListener('DOMContentLoaded', () => {
  // ...既存のコード...
  syncComparisonIconActive();
});

document.addEventListener("DOMContentLoaded", () => {
  // console.log("Compare Products script loaded");
  bootIn(document);
  // 初回全同期
  syncAllButtonsFromStorage(document);
    setTimeout(() => {
    // data-product-cards-root がない商品カードも直接処理
    enhanceProductCards(document.body);
    syncAllButtonsFromStorage(document);
  }, 2000); // 2秒後に再実行
});
